<!-- Copyright (c) 2009-2021. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <cytomine-modal 
    :active="active" 
    :title="$t('annotation-import')" 
    @close="close"
    :width="600"
  >
    <div v-if="!importing">
      <b-message type="is-info" has-icon icon-size="is-small">
        <p>{{ $t('annotation-import-info') }}</p>
        <ul>
          <li>{{ $t('annotation-import-info-geojson') }}</li>
          <li>{{ $t('annotation-import-info-terms') }}</li>
        </ul>
      </b-message>

      <b-field :label="$t('select-files')">
        <div 
          class="file-upload-area"
          :class="{ 'is-dragover': isDragOver }"
          @dragover.prevent="isDragOver = true"
          @dragleave.prevent="isDragOver = false"
          @drop.prevent="handleFileDrop"
        >
          <div class="file-upload-content">
            <i class="fas fa-cloud-upload-alt fa-3x has-text-grey-light"></i>
            <p class="has-text-grey">{{ $t('drag-drop-files') }}</p>
            <p class="has-text-grey is-size-7">{{ $t('or') }}</p>
            <b-button 
              type="is-primary" 
              outlined 
              @click="triggerFileInput"
            >
              {{ $t('add-files') }}
            </b-button>
          </div>
        </div>
        
        <input 
          ref="fileInput"
          type="file" 
          multiple 
          accept=".geojson"
          @change="handleFileSelect"
          style="display: none;"
        />
      </b-field>

      <div v-if="selectedFiles.length > 0" class="selected-files">
        <h4 class="title is-6">{{ $t('selected-files') }} ({{ selectedFiles.length }})</h4>
        <div class="file-list">
          <div 
            v-for="(file, index) in selectedFiles" 
            :key="index"
            class="file-item"
          >
            <div class="file-info">
              <i class="fas fa-file-code has-text-primary"></i>
              <span class="file-name">{{ file.name }}</span>
              <span class="file-size">({{ formatFileSize(file.size) }})</span>
            </div>
            <button 
              class="delete is-small"
              @click="removeFile(index)"
            ></button>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="import-progress">
      <b-loading :active="true" :is-full-page="false"></b-loading>
      <p class="has-text-centered">{{ $t('importing-annotations') }}...</p>
    </div>

    <template #footer>
      <button
        class="button"
        type="button"
        @click="close"
        :disabled="importing"
      >
        {{ $t('button-cancel') }}
      </button>
      <button
        class="button is-primary"
        :class="{ 'is-loading': importing }"
        :disabled="selectedFiles.length === 0 || importing"
        @click="importAnnotations"
      >
        {{ $t('button-import') }}
      </button>
    </template>
  </cytomine-modal>
</template>

<script>
import CytomineModal from '@/components/utils/CytomineModal';
import axios from 'axios';
import { get } from '@/utils/store-helpers';

export default {
  name: 'annotation-import-modal',
  components: {
    CytomineModal
  },
  props: {
    active: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedFiles: [],
      isDragOver: false,
      importing: false,
    };
  },
  computed: {
    project: get('currentProject/project')
  },
  methods: {
    close() {
      if (!this.importing) {
        this.selectedFiles = [];
        this.isDragOver = false;
        this.$emit('close');
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.addFiles(files);
    },

    handleFileDrop(event) {
      this.isDragOver = false;
      const files = Array.from(event.dataTransfer.files);
      this.addFiles(files);
    },

    addFiles(files) {
      const geojsonFiles = files.filter(file => 
        file.name.toLowerCase().endsWith('.geojson')
      );
      
      if (geojsonFiles.length !== files.length) {
        this.$notify({
          type: 'error',
          text: this.$t('only-geojson-files-allowed')
        });
      }

      this.selectedFiles = [...this.selectedFiles, ...geojsonFiles];
    },

    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async importAnnotations() {
      if (this.selectedFiles.length === 0) return;

      this.importing = true;

      try {
        const formData = new FormData();

        formData.append('project_id', this.project.id);
        
        // Add files to form data
        this.selectedFiles.forEach(file => {
          formData.append('files', file);
        });

        const response = await axios.post(
          '/annotations/import',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (response.data.success) {
          this.$notify({
            type: 'success',
            text: this.$t('import-success', {
              count: response.data.imported_annotations,
              total: response.data.total_annotations
            })
          });

          // Emit success event to refresh annotations
          this.$emit('import-success', response.data);
          this.close();
        }
        else {
          this.$notify({
            type: 'error',
            text: this.$t('import-failed', { error: response.data.error })
          });
        }
      }
      catch (error) {
        console.error('Import error:', error);
        
        let errorMessage = this.$t('import-error-unexpected');
        if (error.response && error.response.data) {
          if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          }
          else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          }
        }

        this.$notify({
          type: 'error',
          text: errorMessage
        });
      }
      finally {
        this.importing = false;
      }
    }
  }
};
</script>

<style scoped>
.file-upload-area {
  border: 2px dashed #dbdbdb;
  border-radius: 6px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
}

.file-upload-area:hover,
.file-upload-area.is-dragover {
  border-color: #3273dc;
  background-color: #f8f9ff;
}

.file-upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.selected-files {
  margin-top: 1rem;
}

.file-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  padding: 0.5rem;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  border-bottom: 1px solid #f5f5f5;
}

.file-item:last-child {
  border-bottom: none;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.file-name {
  font-weight: 500;
}

.file-size {
  color: #7a7a7a;
  font-size: 0.875rem;
}

.import-progress {
  text-align: center;
  padding: 2rem;
}
</style>
